import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { FOREIGN_COUNTRIES, countryName } from "@/lib/countries";

interface Props {
  /** Valgt ISO 3166-1 alpha-2-kode, eller tom streng før noe er valgt. */
  value: string;
  onChange: (code: string) => void;
  id?: string;
}

/**
 * Søkbar landvelger for utenlandske kunder.
 *
 * Listen er hele ISO 3166-1 (248 land utenom Norge), fordi PowerOffice
 * godtar hele standarden. Det er for mange for en <select>, så vi bruker
 * Popover+Command med fritekst-søk — samme mønster som CustomerPicker og
 * TemplatePicker. Det søkbare feltet inkluderer koden, så «SE» treffer
 * Sverige like godt som navnet.
 */
export function CountryPicker({ value, onChange, id }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label="Land"
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={value ? "" : "text-muted-foreground"}>
            {value ? countryName(value) : "Velg land"}
          </span>
          <SrIcon name="chevron-down" className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Søk etter land..." className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>Ingen land matcher søket.</CommandEmpty>
            <CommandGroup>
              {FOREIGN_COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="truncate">{c.name}</span>
                    <span className="text-xs text-muted-foreground ml-2 shrink-0 tabular-nums">
                      {c.code}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
